import { DisplayNameSchema, type UserPrivate } from '@runway/shared';
import { useCallback, useEffect, useRef, useState, type FormEvent, type ReactNode } from 'react';
import { NavLink, Outlet, useLocation, useNavigate } from 'react-router-dom';
import { ChatDrawer } from '../features/chat/ChatDrawer';
import { UserAvatar } from '../features/friends/UserAvatar';
import { HubNotifications } from '../features/home/HubNotifications';
import { InvitesList } from '../features/home/InvitesList';
import { ApiError, api } from '../net/api';
import { selectPendingInviteCount, selectTotalUnread, useHub } from '../state/hub';
import { useSession } from '../state/session';
import { Button } from './Button';
import { Spinner } from './Spinner';
import { toast } from './Toast';
import { cx } from './cx';

const LINKS: Array<{ to: string; label: string; icon: ReactNode; devOnly?: boolean }> = [
  { to: '/', label: 'Home', icon: <HomeIcon /> },
  { to: '/wardrobe', label: 'Wardrobe', icon: <HangerIcon /> },
  { to: '/friends', label: 'Friends', icon: <FriendsIcon /> },
  { to: '/lab', label: 'Lab', icon: <LabIcon />, devOnly: true },
];

/**
 * Signed-in shell: sticky top bar (wordmark, nav, hub status, invites bell,
 * messages, user menu), mobile bottom tab bar, live notifications and the chat
 * drawer. Connects the hub once the session is authed and tears it down on logout.
 */
export function AppShell() {
  const user = useSession((s) => s.user);
  const status = useSession((s) => s.status);
  const connect = useHub((s) => s.connect);
  const disconnect = useHub((s) => s.disconnect);

  useEffect(() => {
    if (status !== 'authed' || !user) return;
    void connect();
    return () => disconnect();
    // Reconnect when the signed-in user changes (login as someone else).
  }, [status, user?.id, connect, disconnect]);

  return (
    <>
      <header className="nav">
        <div className="container nav-inner">
          <NavLink to="/" className="brand nav-brand" aria-label="RUNWAY home">
            <span className="brand-spark" aria-hidden="true">
              ✦
            </span>
            RUNWAY
          </NavLink>
          <nav className="nav-links" aria-label="Primary">
            {LINKS.filter((l) => !l.devOnly || import.meta.env.DEV).map((l) => (
              <NavLink key={l.to} to={l.to} end={l.to === '/'} className={({ isActive }) => cx('nav-link', isActive && 'active')}>
                {l.label}
              </NavLink>
            ))}
          </nav>
          <div className="nav-right">
            <HubStatusChip />
            <InvitesBell />
            <MessagesButton />
            {user ? <UserMenu user={user} /> : null}
          </div>
        </div>
      </header>

      <main className="container page">
        <Outlet />
      </main>

      <TabBar />
      <HubNotifications />
      <ChatDrawer />
    </>
  );
}

// ── hub status ─────────────────────────────────────────────────────────────

function HubStatusChip() {
  const status = useHub((s) => s.status);
  const attempt = useHub((s) => s.reconnectAttempt);
  // Do not flash on the very first connect; only show once we have been online or retried.
  const [seenOnline, setSeenOnline] = useState(false);
  useEffect(() => {
    if (status === 'online') setSeenOnline(true);
  }, [status]);
  if (status === 'online') return null;
  if (!seenOnline && attempt === 0) return null;
  return (
    <span className={cx('hub-chip', status === 'offline' && 'hub-chip-offline')} role="status" title="Live features (presence, invites, chat) are reconnecting">
      {status === 'connecting' ? <Spinner size={12} /> : null}
      {status === 'connecting' ? 'Reconnecting' : 'Offline'}
    </span>
  );
}

// ── invites bell ───────────────────────────────────────────────────────────

function InvitesBell() {
  const count = useHub(selectPendingInviteCount);
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  useClickOutside(ref, () => setOpen(false), open);
  const [ring, setRing] = useState(false);
  const prev = useRef(count);
  useEffect(() => {
    const increased = count > prev.current;
    prev.current = count;
    if (!increased) return undefined;
    setRing(true);
    const t = setTimeout(() => setRing(false), 1000);
    return () => clearTimeout(t);
  }, [count]);

  return (
    <div className="popover-anchor" ref={ref}>
      <button
        type="button"
        className={cx('nav-icon-btn', ring && 'bell-ring')}
        aria-label={count ? `${count} pending invitation${count === 1 ? '' : 's'}` : 'Invitations'}
        aria-haspopup="dialog"
        aria-expanded={open}
        onClick={() => setOpen((o) => !o)}
      >
        <BellIcon />
        {count > 0 ? <span className="badge badge-gold">{count > 9 ? '9+' : count}</span> : null}
      </button>
      {open ? (
        <div className="popover" role="dialog" aria-label="Invitations">
          <div className="popover-title">
            <span>Invitations</span>
            {count > 0 ? <span className="chip chip-gold">{count} pending</span> : null}
          </div>
          <InvitesList compact onNavigate={() => setOpen(false)} />
        </div>
      ) : null}
    </div>
  );
}

// ── messages button ────────────────────────────────────────────────────────

function MessagesButton() {
  const unread = useHub(selectTotalUnread);
  const openChat = useHub((s) => s.openChat);
  const chatOpen = useHub((s) => s.chatOpen);
  const closeChat = useHub((s) => s.closeChat);
  return (
    <button
      type="button"
      className="nav-icon-btn"
      aria-label={unread ? `${unread} unread message${unread === 1 ? '' : 's'}` : 'Messages'}
      aria-expanded={chatOpen}
      onClick={() => (chatOpen ? closeChat() : openChat())}
    >
      <ChatIcon />
      {unread > 0 ? <span className="badge">{unread > 99 ? '99+' : unread}</span> : null}
    </button>
  );
}

// ── user menu (avatar, inline name edit, logout) ───────────────────────────

function UserMenu({ user }: { user: UserPrivate }) {
  const logout = useSession((s) => s.logout);
  const setUser = useSession((s) => s.setUser);
  const disconnect = useHub((s) => s.disconnect);
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(false);
  const [name, setName] = useState(user.displayName);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const ref = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  useClickOutside(
    ref,
    () => {
      setOpen(false);
      setEditing(false);
    },
    open,
  );

  useEffect(() => {
    if (editing) inputRef.current?.select();
  }, [editing]);
  useEffect(() => {
    if (!editing) setName(user.displayName);
  }, [user.displayName, editing]);

  const save = async () => {
    if (saving) return;
    const parsed = DisplayNameSchema.safeParse(name);
    if (!parsed.success) {
      setError(parsed.error.issues[0]?.message ?? 'Invalid name');
      return;
    }
    if (parsed.data === user.displayName) {
      setEditing(false);
      return;
    }
    setSaving(true);
    setError(null);
    try {
      const res = await api.patch<{ user: UserPrivate }>('/api/me', { displayName: parsed.data });
      setUser(res.user);
      setEditing(false);
      toast.success(`You are now ${res.user.displayName} ✨`);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Could not save your name');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="popover-anchor" ref={ref}>
      <button type="button" className="user-chip" aria-haspopup="menu" aria-expanded={open} aria-label="Your account" onClick={() => setOpen((o) => !o)}>
        <UserAvatar displayName={user.displayName} picture={user.picture} avatar={user.avatar} size="sm" />
        <span className="user-chip-name">{user.displayName}</span>
      </button>
      {open ? (
        <div className="popover" role="menu" aria-label="Account">
          <div className="row" style={{ alignItems: 'center', marginBottom: 'var(--sp-3)', flexWrap: 'nowrap' }}>
            <UserAvatar displayName={user.displayName} picture={user.picture} avatar={user.avatar} size="lg" />
            <div style={{ minWidth: 0, flex: 1 }}>
              {editing ? (
                <form
                  className="stack stack-sm"
                  onSubmit={(e: FormEvent) => {
                    e.preventDefault();
                    void save();
                  }}
                >
                  <div className="name-edit">
                    <input
                      ref={inputRef}
                      className={cx('input', error && 'input-error')}
                      value={name}
                      onChange={(e) => {
                        setName(e.target.value);
                        setError(null);
                      }}
                      onKeyDown={(e) => {
                        if (e.key === 'Escape') {
                          setEditing(false);
                          setError(null);
                        } else if (e.key === 'Enter') {
                          e.preventDefault();
                          void save();
                        }
                      }}
                      maxLength={24}
                      aria-label="Display name"
                      autoComplete="nickname"
                    />
                    <Button type="submit" size="sm" variant="gold" loading={saving}>
                      Save
                    </Button>
                  </div>
                  {error ? (
                    <span className="help help-error" role="alert">
                      {error}
                    </span>
                  ) : (
                    <span className="help">2–24 characters. Press Esc to cancel.</span>
                  )}
                </form>
              ) : (
                <>
                  <button type="button" className="name-button" onClick={() => setEditing(true)} title="Edit your display name">
                    <span style={{ overflow: 'hidden', textOverflow: 'ellipsis' }}>{user.displayName}</span>
                    <span className="pencil" aria-hidden="true">
                      ✎
                    </span>
                  </button>
                  <div className="small muted" style={{ overflow: 'hidden', textOverflow: 'ellipsis' }}>
                    {user.email ?? (user.isDevAccount ? 'Local account' : '')}
                  </div>
                </>
              )}
            </div>
          </div>
          <button
            type="button"
            className="menu-item"
            role="menuitem"
            onClick={() => {
              setOpen(false);
              navigate('/wardrobe');
            }}
          >
            <HangerIcon /> Edit my avatar
          </button>
          <button
            type="button"
            className="menu-item menu-item-danger"
            role="menuitem"
            onClick={async () => {
              setOpen(false);
              disconnect();
              await logout();
              navigate('/login', { replace: true });
            }}
          >
            <LogoutIcon /> Log out
          </button>
        </div>
      ) : null}
    </div>
  );
}

// ── mobile tab bar ─────────────────────────────────────────────────────────

function TabBar() {
  const unread = useHub(selectTotalUnread);
  const openChat = useHub((s) => s.openChat);
  const chatOpen = useHub((s) => s.chatOpen);
  const closeChat = useHub((s) => s.closeChat);
  const location = useLocation();
  return (
    <nav className="tabbar" aria-label="Primary (mobile)">
      {LINKS.filter((l) => !l.devOnly).map((l) => {
        const active = !chatOpen && (l.to === '/' ? location.pathname === '/' : location.pathname.startsWith(l.to));
        return (
          <NavLink key={l.to} to={l.to} end={l.to === '/'} className={cx('tab', active && 'active')} onClick={() => closeChat()}>
            {l.icon}
            {l.label}
          </NavLink>
        );
      })}
      <button type="button" className={cx('tab', chatOpen && 'active')} onClick={() => (chatOpen ? closeChat() : openChat())} aria-label="Chat">
        <ChatIcon />
        Chat
        {unread > 0 ? <span className="badge">{unread > 99 ? '99+' : unread}</span> : null}
      </button>
    </nav>
  );
}

// ── helpers ────────────────────────────────────────────────────────────────

function useClickOutside(ref: React.RefObject<HTMLElement | null>, onOutside: () => void, active: boolean) {
  const cb = useCallback(onOutside, [onOutside]);
  useEffect(() => {
    if (!active) return;
    const onDown = (e: MouseEvent | TouchEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) cb();
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') cb();
    };
    document.addEventListener('mousedown', onDown);
    document.addEventListener('touchstart', onDown);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDown);
      document.removeEventListener('touchstart', onDown);
      document.removeEventListener('keydown', onKey);
    };
  }, [ref, cb, active]);
}

// ── icons (inline, no deps) ────────────────────────────────────────────────

function HomeIcon() {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="M3 11.5 12 4l9 7.5" />
      <path d="M5 10v10h5v-6h4v6h5V10" />
    </svg>
  );
}
function HangerIcon() {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="M12 3a2.5 2.5 0 0 1 1 4.8V10l8 5.5c.9.6.5 2-.6 2H3.6c-1.1 0-1.5-1.4-.6-2L11 10V8" />
    </svg>
  );
}
function FriendsIcon() {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <circle cx="9" cy="8" r="3.5" />
      <path d="M2.5 20a6.5 6.5 0 0 1 13 0" />
      <circle cx="17" cy="9" r="2.5" />
      <path d="M16 15.5a5 5 0 0 1 5.5 4.5" />
    </svg>
  );
}
function LabIcon() {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="M9 3h6M10 3v6l-5.5 9.5A2 2 0 0 0 6.2 21h11.6a2 2 0 0 0 1.7-2.5L14 9V3" />
    </svg>
  );
}
function BellIcon() {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="M6 16V11a6 6 0 0 1 12 0v5l1.5 2H4.5L6 16Z" />
      <path d="M10 20a2 2 0 0 0 4 0" />
    </svg>
  );
}
function ChatIcon() {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="M4 5.5A2.5 2.5 0 0 1 6.5 3h11A2.5 2.5 0 0 1 20 5.5v8a2.5 2.5 0 0 1-2.5 2.5H10l-5 4v-4H6.5A2.5 2.5 0 0 1 4 13.5v-8Z" />
    </svg>
  );
}
function LogoutIcon() {
  return (
    <svg viewBox="0 0 24 24" width="20" height="20" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="M10 4H6a2 2 0 0 0-2 2v12a2 2 0 0 0 2 2h4" />
      <path d="M15 8l4 4-4 4M19 12H9" />
    </svg>
  );
}
